import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton, Icon, Tooltip, Typography, Button } from '@material-ui/core';
import { HelpOutline } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import SupportDialog from './SupportDialog';
import * as WalkthroughActions from 'app/main/walkthrough/store/actions/walkthrough.actions';

var useStyles = makeStyles(function () {
    return {
        menu: {
            position: 'absolute',
            right: 0,
            top: '100%',
            marginTop: 4,
            width: 220,
            backgroundColor: '#1e293b',
            borderRadius: 10,
            boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
            border: '1px solid #334155',
            overflow: 'hidden',
            zIndex: 9999
        },
        item: {
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            width: '100%',
            padding: '10px 16px',
            fontSize: 13,
            fontWeight: 500,
            color: '#e2e8f0',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'background-color 0.15s',
            '&:hover': {
                backgroundColor: '#334155'
            }
        },
        icon: {
            fontSize: 18,
            color: '#94a3b8'
        },
        divider: {
            height: 1,
            backgroundColor: '#334155',
            margin: '2px 0'
        },
        label: {
            padding: '8px 16px 4px',
            fontSize: 10,
            fontWeight: 600,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
        },
        backdrop: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9998
        },
        noClusterMsg: {
            padding: '8px 16px 12px',
            fontSize: 11,
            color: '#f59e0b',
            lineHeight: 1.4
        }
    };
});

var DOCS_URL = 'https://heidiai.io/docs';
var CONTACT_URL = 'https://heidiai.io/contact-us';
// Self-paced course covering Heidi end to end. Free to take, no approval, and the
// public course page works signed out, so it is safe to hand to any role.
var BOOTCAMP_URL = 'https://heidiai.io/courses/heidi-higher-ed-hpc-ai-bootcamp';

var HelpMenu = function () {
    var classes = useStyles();
    var dispatch = useDispatch();
    var [open, setOpen] = useState(false);
    var [supportOpen, setSupportOpen] = useState(false);
    var [showNoCluster, setShowNoCluster] = useState(false);
    var ref = useRef(null);

    var clusters = useSelector(function (s) {
        return s.clusters && s.clusters.cluster && s.clusters.cluster.list;
    }) || [];

    var role = useSelector(function (s) { return s.auth && s.auth.user && s.auth.user.role; }) || [];
    var isParticipant = role.indexOf('participant') !== -1;

    var handleDocs = function () {
        setOpen(false);
        window.open(DOCS_URL, '_blank');
    };

    var handleContact = function () {
        setOpen(false);
        window.open(CONTACT_URL, '_blank');
    };

    var handleBootcamp = function () {
        setOpen(false);
        window.open(BOOTCAMP_URL, '_blank');
    };

    var handleReplayTour = function () {
        // Check if user has at least one deployed cluster
        var hasDeployed = clusters.some(function (c) {
            return c.info && c.info.state === 'available';
        });

        if (!hasDeployed && clusters.length > 0) {
            setShowNoCluster(true);
            return;
        }

        setOpen(false);
        setShowNoCluster(false);
        // Reset walkthrough state and start
        localStorage.removeItem('walkthrough_completed');
        dispatch(WalkthroughActions.resetWalkthroughForUser());
        setTimeout(function () {
            dispatch(WalkthroughActions.startWalkthrough());
        }, 500);
    };

    // Close menu on outside click
    useEffect(function () {
        if (!open) {
            setShowNoCluster(false);
        }
    }, [open]);

    return (
        <div style={{ position: 'relative' }} ref={ref}>
            <Tooltip title="Help" placement="bottom">
                <IconButton
                    onClick={function () { setOpen(!open); }}
                    color="inherit"
                    style={{ marginRight: 4 }}
                    aria-label="Help"
                    data-walkthrough="help-menu"
                >
                    <HelpOutline />
                    <Typography style={{ fontSize: 13, fontWeight: 500, marginLeft: 4, color: 'inherit' }}>
                        Help
                    </Typography>
                </IconButton>
            </Tooltip>

            {open && (
                <React.Fragment>
                    <div className={classes.backdrop} onClick={function () { setOpen(false); }} />
                    <div className={classes.menu}>
                        <button className={classes.item} onClick={handleDocs}>
                            <Icon className={classes.icon}>menu_book</Icon>
                            Help & Docs
                        </button>
                        <button className={classes.item} onClick={handleContact}>
                            <Icon className={classes.icon}>mail_outline</Icon>
                            Contact Us
                        </button>
                        <button className={classes.item} onClick={handleBootcamp}>
                            <Icon className={classes.icon}>school</Icon>
                            Heidi AI Bootcamp
                        </button>
                        {!isParticipant && (
                            <React.Fragment>
                                <div className={classes.divider} />
                                <button className={classes.item} onClick={handleReplayTour}>
                                    <Icon className={classes.icon}>replay</Icon>
                                    Replay Tour
                                </button>
                                {showNoCluster && (
                                    <div className={classes.noClusterMsg}>
                                        <Icon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4, color: '#f59e0b' }}>warning</Icon>
                                        Deploy a cluster first for the complete tour experience.
                                    </div>
                                )}
                            </React.Fragment>
                        )}
                    </div>
                </React.Fragment>
            )}

            <SupportDialog
                open={supportOpen}
                onClose={function () { setSupportOpen(false); }}
            />
        </div>
    );
};

export default HelpMenu;
